const SITE_URL = 'https://xadevs.com';

export const siteMeta = {
  siteName: 'XADevs LLC',
  alternateName: 'XADevs',
  siteUrl: SITE_URL,
  logoPath: '/android-chrome-512x512.png',
  defaultTitle: 'XADevs LLC | AI Products & Software Systems',
  titleTemplate: '%s | XADevs LLC',
  defaultDescription:
    'XADevs LLC builds AI products and software systems, including VolpeTrack, an AI-powered finance assistant, and HoldThen, a B2B logistics platform.',
  organizationDescription:
    'XADevs LLC is a US-registered software company building AI products and software systems: AI-native product features, cloud backends, and cross-platform apps such as VolpeTrack and HoldThen.',
  socialProfiles: [
    'https://x.com/xadevs',
    'https://github.com/xadevs',
    'https://linkedin.com/company/xadevs'
  ],
  importantRoutes: {
    home: '/',
    productsSection: '/#products',
    aboutSection: '/#about',
    contactSection: '/#contact',
    support: '/support',
    privacyPolicy: '/privacy-policy',
    termsOfService: '/terms-of-service',
    sitemap: '/sitemap',
    volpeTrackPrivacy: '/volpetrack/privacy',
    volpeTrackTerms: '/volpetrack/terms',
    volpeTrackAccountDeletion: '/volpetrack/account-deletion'
  },
  productUrls: {
    volpeTrackIOS: 'https://apps.apple.com/us/app/volpetrack-ai-budget-tracker/id6748869391',
    volpeTrackAndroid: 'https://play.google.com/store/apps/details?id=com.volpetrack.mobile',
    holdThenWebsite: 'https://www.holdthen.com/'
  },
  routeSeo: {
    home: {
      title: 'XADevs LLC | AI Products & Software Systems',
      description:
        'XADevs LLC builds AI products and software systems. Explore VolpeTrack, an AI-powered finance assistant, and HoldThen, a B2B logistics platform.',
      path: '/'
    },
    support: {
      title: 'Support Center',
      description:
        'Get support for XADevs products, including VolpeTrack and HoldThen, through the official support center.',
      path: '/support'
    },
    privacyPolicy: {
      title: 'Privacy Policy',
      description:
        'Read the XADevs LLC privacy policy for website usage, data handling, and communication preferences.',
      path: '/privacy-policy'
    },
    termsOfService: {
      title: 'Terms of Service',
      description:
        'Read the XADevs LLC terms of service for website use, intellectual property, and legal conditions.',
      path: '/terms-of-service'
    },
    sitemap: {
      title: 'Sitemap',
      description:
        'Browse the XADevs sitemap to access company pages, product pages, support, and legal documentation.',
      path: '/sitemap'
    },
    volpeTrackPrivacy: {
      title: 'VolpeTrack Privacy Policy',
      description:
        'Read the VolpeTrack privacy policy by XADevs LLC, including data processing, retention, and user rights.',
      path: '/volpetrack/privacy'
    },
    volpeTrackTerms: {
      title: 'VolpeTrack Terms and Conditions',
      description:
        'Read VolpeTrack terms and conditions, including plans, billing, and usage rules provided by XADevs LLC.',
      path: '/volpetrack/terms'
    },
    volpeTrackAccountDeletion: {
      title: 'VolpeTrack Account Deletion',
      description:
        'Find official instructions to delete your VolpeTrack account and understand how XADevs LLC processes data removal.',
      path: '/volpetrack/account-deletion'
    },
    notFound: {
      title: 'Page Not Found',
      description:
        'The page you requested could not be found. Use the XADevs navigation to find products, support, or company pages.',
      path: '/404'
    }
  }
};

const ensureLeadingSlash = (path = '/') => (path.startsWith('/') ? path : `/${path}`);

export const buildCanonicalUrl = (path = '/') => {
  const normalizedPath = ensureLeadingSlash(path === '' ? '/' : path);
  return new URL(normalizedPath, siteMeta.siteUrl).toString();
};

export const buildPageTitle = (title) => {
  if (!title || title === siteMeta.defaultTitle) {
    return siteMeta.defaultTitle;
  }

  return siteMeta.titleTemplate.replace('%s', title);
};

export const homepageStructuredData = [
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteMeta.siteName,
    alternateName: siteMeta.alternateName,
    url: siteMeta.siteUrl,
    logo: buildCanonicalUrl(siteMeta.logoPath),
    description: siteMeta.organizationDescription,
    sameAs: siteMeta.socialProfiles,
    knowsAbout: [
      'Artificial intelligence',
      'Generative AI',
      'Software engineering',
      'Cloud infrastructure',
      'Mobile applications',
      'Financial technology',
      'Logistics technology'
    ]
  },
  {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'VolpeTrack',
    applicationCategory: 'FinanceApplication',
    operatingSystem: 'iOS, Android',
    description:
      'AI-powered personal finance assistant with smart expense categorization, spending analytics, and AI-driven financial insights.',
    author: {
      '@type': 'Organization',
      name: siteMeta.siteName,
      url: siteMeta.siteUrl
    },
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD'
    },
    installUrl: [siteMeta.productUrls.volpeTrackIOS, siteMeta.productUrls.volpeTrackAndroid],
    url: buildCanonicalUrl('/#products')
  },
  {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'HoldThen',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    description:
      'B2B international logistics platform unifying package forwarding, consolidation, inventory, billing, and end-to-end shipment tracking.',
    author: {
      '@type': 'Organization',
      name: siteMeta.siteName,
      url: siteMeta.siteUrl
    },
    url: siteMeta.productUrls.holdThenWebsite
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteMeta.siteName,
    alternateName: siteMeta.alternateName,
    url: siteMeta.siteUrl,
    publisher: {
      '@type': 'Organization',
      name: siteMeta.siteName,
      url: siteMeta.siteUrl
    }
  }
];
